/**
 * Shop screen — buy cards/Programs, pay to remove a card, then leave.
 *
 * The stock + prices are generated deterministically by the engine and live on
 * run.shop. Purchases go through store.buyCard/buyProgram/buyRemove; leaving via
 * store.leaveShop. A removal flow opens a deck picker.
 */

import { el, div, button } from "../dom";
import { topbar, cardFace, cardRow } from "../widgets";
import { getProgramDef, removalPrice } from "../../engine";
import type { Store } from "../store";

let removing = false;

export function resetShopUi(): void {
  removing = false;
}

export function renderShop(store: Store): HTMLElement {
  const run = store.run!;
  const shop = run.shop!;

  const screen = div("screen");
  screen.appendChild(topbar(run, () => store.open("deck")));

  const body = div("screen__body scroll");

  body.appendChild(
    div("row", [
      el("div", { class: "h-title", text: "BLACK MARKET" }),
      div("fill"),
      el("div", {
        class: "h-sub",
        text: `${run.gold} ₵`,
        style: "color:var(--amber)",
      }),
    ]),
  );

  if (removing) {
    body.appendChild(renderRemovePicker(store));
    screen.appendChild(body);
    return screen;
  }

  // --- Cards row ----------------------------------------------------------
  body.appendChild(el("div", { class: "h-sub", text: "WARES" }));
  const cards = div("row wrap");
  for (const item of shop.cards) {
    const affordable = run.gold >= item.price && !item.purchased;
    const col = div("col");
    col.style.alignItems = "center";
    col.style.width = "118px";
    col.appendChild(
      cardFace(item.defId, {
        affordable: run.gold >= item.price,
        onClick: item.purchased
          ? undefined
          : () => {
              if (affordable) store.buyCard(item.slot);
            },
      }),
    );
    col.appendChild(
      item.purchased
        ? el("div", { class: "sold", text: "SOLD", style: "width:auto" })
        : button(
            affordable ? "btn btn--sm" : "btn btn--sm",
            `${item.price} ₵`,
            () => store.buyCard(item.slot),
            { disabled: !affordable },
          ),
    );
    cards.appendChild(col);
  }
  body.appendChild(cards);

  // --- Programs -----------------------------------------------------------
  body.appendChild(el("div", { class: "h-sub", text: "PROGRAMS", style: "color:var(--magenta)" }));
  const progs = div("col");
  for (const item of shop.programs) {
    const p = getProgramDef(item.defId);
    const affordable = run.gold >= item.price && !item.purchased;
    const row = div("panel shop-item", [
      div("fill", [
        el("div", { class: "h-sub", text: p.name, style: "color:var(--magenta);font-size:13px" }),
        el("div", {
          class: "muted",
          text: p.description,
          style: "white-space:normal;line-height:1.35;font-size:11px",
        }),
      ]),
      item.purchased
        ? el("div", { class: "sold", text: "SOLD" })
        : button(
            "btn btn--sm",
            `${item.price} ₵`,
            () => store.buyProgram(item.slot),
            { disabled: !affordable },
          ),
    ]);
    progs.appendChild(row);
  }
  if (shop.programs.length === 0) {
    progs.appendChild(el("div", { class: "muted", text: "No Programs in stock." }));
  }
  body.appendChild(progs);

  // --- Services -----------------------------------------------------------
  const rmPrice = removalPrice(shop);
  const canRemove = run.gold >= rmPrice && run.deck.length > 0;
  body.appendChild(el("div", { class: "h-sub", text: "SERVICES" }));
  body.appendChild(
    div("panel shop-item", [
      div("fill", [
        el("div", { class: "h-sub", text: "PURGE A CARD", style: "font-size:13px" }),
        el("div", { class: "muted", text: "Permanently remove a card from your deck." }),
      ]),
      button("btn btn--sm", `${rmPrice} ₵`, () => {
        removing = true;
        store.requestRender();
      }, { disabled: !canRemove }),
    ]),
  );

  screen.appendChild(body);

  const actions = div("row");
  actions.style.justifyContent = "flex-end";
  actions.style.flex = "0 0 auto";
  actions.style.paddingTop = "var(--u)";
  actions.style.borderTop = "2px solid var(--bg-3)";
  actions.appendChild(
    button("btn btn--primary", "LEAVE ▶", () => {
      resetShopUi();
      store.leaveShop();
    }),
  );
  screen.appendChild(actions);
  return screen;
}

function renderRemovePicker(store: Store): HTMLElement {
  const run = store.run!;
  const wrap = div("col");
  wrap.appendChild(el("div", { class: "h-sub", text: "PURGE WHICH CARD?" }));
  const list = div("col scroll");
  list.style.maxHeight = "360px";
  for (const card of run.deck) {
    list.appendChild(
      cardRow(card.defId, {
        onClick: () => {
          removing = false;
          store.buyRemove(card.uid);
        },
      }),
    );
  }
  wrap.appendChild(list);
  wrap.appendChild(
    button("btn btn--ghost", "← cancel", () => {
      removing = false;
      store.requestRender();
    }),
  );
  return wrap;
}
